import { HTTPError } from 'ky';
import type { JiraImportWizardSubmissionSchema } from '@/components/common/settings/work-integrations/work-integration-dialog/jira/lib/types';
import { tempoApi } from '@/lib/api/tempoApi';
import {
  type ExternalWorkIntegrationStatusValues,
  type IntegrationCustomFieldMapping,
  type OAuthCredentialStatusResponse,
  type PendingItemsCountResponse,
  type SyncDirection,
  SyncType,
  type WorkIntegrationFilters,
  type WorkIntegrationResponse,
  type WorkIntegrationSyncErrorResponse,
  type WorkIntegrationSyncRequest,
} from '@/lib/api/types/work-integrations';

export type SyncWorkIntegrationResult =
  | { type: 'queued'; workIntegration: WorkIntegrationResponse }
  | { type: 'no-items' }
  | { type: 'no-field-mappings' };

const getAllWorkIntegrations = async (options?: {
  limit?: number;
  filter?: {
    statuses?: ExternalWorkIntegrationStatusValues[];
    ids?: number[];
    loopIds?: number[];
  };
}) => {
  const searchParams = new URLSearchParams();

  if (options?.limit) {
    searchParams.append('limit', options.limit.toString());
  }

  if (options?.filter?.statuses?.length) {
    options.filter.statuses.forEach((status) => {
      searchParams.append('statuses', status);
    });
  }

  if (options?.filter?.ids?.length) {
    searchParams.append('ids', options.filter.ids.join(','));
  }

  if (options?.filter?.loopIds?.length) {
    searchParams.append('loopIds', options.filter.loopIds.join(','));
  }

  const url = `api/v1/work-integrations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  const response = await tempoApi().get(url).json<WorkIntegrationResponse[]>();
  return response;
};

const syncWorkIntegration = async ({
  workIntegrationId,
  payload,
}: {
  workIntegrationId: number;
  payload: WorkIntegrationSyncRequest;
}): Promise<SyncWorkIntegrationResult> => {
  const endpoint = payload.syncType === SyncType.PULL ? 'pull' : 'push';

  try {
    const response = await tempoApi().post(
      `api/v1/work-integrations/${workIntegrationId}/${endpoint}`,
    );

    if (response.status === 204) {
      return { type: 'no-items' };
    }

    return { type: 'queued', workIntegration: await response.json<WorkIntegrationResponse>() };
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 400) {
      const errorData = await error.response.json<{ message: string }>();
      if (errorData.message === 'NO_FIELD_MAPPINGS') {
        return { type: 'no-field-mappings' };
      }
    }
    throw error;
  }
};

const deleteWorkIntegration = async ({ workIntegrationId }: { workIntegrationId: number }) => {
  return tempoApi().delete(`api/v1/work-integrations/${workIntegrationId}`);
};

const submitWorkIntegration = async ({
  payload,
}: {
  payload: JiraImportWizardSubmissionSchema;
}) => {
  const response = await tempoApi()
    .post('api/v1/work-integrations', {
      json: payload,
    })
    .json<WorkIntegrationResponse>();
  return response;
};

const getWorkIntegrationErrors = async ({ workIntegrationId }: { workIntegrationId: number }) => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/${workIntegrationId}/errors`)
    .json<WorkIntegrationSyncErrorResponse>();
  return response;
};

const updateWorkIntegrationCredentials = async ({
  workIntegrationId,
  oauthCredentialsId,
}: {
  workIntegrationId: number;
  oauthCredentialsId: number;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(
      `api/v1/work-integrations/${workIntegrationId}/credentials?oauthCredentialsId=${oauthCredentialsId}`,
    )
    .json<WorkIntegrationResponse>();
  return response;
};

const updateWorkIntegrationFilters = async ({
  integrationId,
  filters,
}: {
  integrationId: number;
  filters: WorkIntegrationFilters;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(`api/v1/work-integrations/${integrationId}/filters`, { json: filters })
    .json<WorkIntegrationResponse>();
  return response;
};

const toggleSynchronizationStatus = async ({
  workIntegrationId,
}: {
  workIntegrationId: number;
}): Promise<WorkIntegrationResponse> => {
  const response = await tempoApi()
    .patch(`api/v1/work-integrations/${workIntegrationId}/synchronization-status`)
    .json<WorkIntegrationResponse>();
  return response;
};

const updateFieldMappings = async ({
  integrationId,
  standardFieldsMappings,
  customFieldMappings,
}: {
  integrationId: number;
  standardFieldsMappings: {
    estimateFieldId?: string | null;
    teamFieldId?: string | null;
    expectedStartDateFieldId?: string | null;
    expectedFinishDateFieldId?: string | null;
    plannedStartDateFieldId?: string | null;
    plannedFinishDateFieldId?: string | null;
  };
  customFieldMappings: { instanceFieldMappingId: number; syncDirection: SyncDirection }[];
}): Promise<void> => {
  await tempoApi().put(`api/v1/work-integrations/${integrationId}/field-mappings`, {
    json: { standardFieldsMappings, customFieldMappings },
  });
};

const getIntegrationCustomFieldMappings = async ({
  integrationId,
}: {
  integrationId: number;
}): Promise<IntegrationCustomFieldMapping[]> => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/${integrationId}/custom-field-mappings`)
    .json<IntegrationCustomFieldMapping[]>();
  return response;
};

const getPendingItemsCount = async ({
  integrationId,
}: {
  integrationId: number;
}): Promise<PendingItemsCountResponse> => {
  return await tempoApi()
    .get(`api/v1/work-integration/integrations/${integrationId}/pending-count`)
    .json<PendingItemsCountResponse>();
};

const getOAuthCredentialStatuses = async (
  credentialIds: number[],
): Promise<OAuthCredentialStatusResponse> => {
  if (credentialIds.length === 0) {
    return { statuses: [] };
  }

  const searchParams = new URLSearchParams();
  credentialIds.forEach((credentialId) => {
    searchParams.append('ids', credentialId.toString());
  });

  const response = await tempoApi()
    .get(`api/v1/work-integration/oauth-credentials/status?${searchParams.toString()}`)
    .json<OAuthCredentialStatusResponse>();

  return response;
};

export const WorkIntegrationsApi = {
  submitWorkIntegration,
  getAllWorkIntegrations,
  syncWorkIntegration,
  deleteWorkIntegration,
  getWorkIntegrationErrors,
  updateWorkIntegrationCredentials,
  updateWorkIntegrationFilters,
  updateFieldMappings,
  toggleSynchronizationStatus,
  getOAuthCredentialStatuses,
  getIntegrationCustomFieldMappings,
  getPendingItemsCount,
};
